/**
 * La file du soir, adossée à Supabase.
 *
 * Ce dépôt ne lit presque rien lui-même : les factures impayées viennent du
 * dépôt factures, les chantiers du dépôt chantiers, les devis du dépôt devis,
 * la cadence de la configuration. C'est le parti du planning et du pilotage,
 * pour la même raison — une facture échue affichée dans la file doit être la
 * facture échue affichée sur l'écran des factures, au centime et au jour près.
 *
 * Il n'ajoute que les lectures qui n'existaient nulle part :
 *
 *   · le **dernier palier de relance réellement parti**, dans
 *     `reminder_actions`. C'est lui qui empêche la file de proposer ce que le
 *     cron de 8 h a déjà expédié le matin même ;
 *   · le **devis signé** d'un chantier, sans lequel aucun pourcentage
 *     d'avancement n'est chiffrable en euros ;
 *   · la **dernière relance de devis partie**, dans `document_sends`, pour ne
 *     pas relancer deux fois la même semaine ;
 *   · le **fournisseur** d'un article sous son seuil, sans qui le bon de
 *     commande n'a nulle part où partir.
 */
import {
  DomainError,
  OPEN_PROJECT_STATUSES,
  buildEveningQueue,
  clientDisplayName,
} from "@dker/core";
import type {
  EveningItem,
  EveningRepository,
  ExecutionContext,
  InvoiceRepository,
  LowStockSignal,
  OverdueInvoiceSignal,
  PendingQuoteSignal,
  ProjectProgressSignal,
  ProjectRepository,
  QuoteRepository,
  TenantConfigRepository,
} from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const num = (v: unknown): number => (v == null ? 0 : Number(v));

const DAY_MS = 86_400_000;

/** Jours pleins écoulés depuis un instant, jamais négatifs. */
function daysSince(iso: string | null): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / DAY_MS));
}

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

/**
 * La validité retenue quand le devis n'en porte pas.
 *
 * Identique au défaut de la colonne `quotes.validity_days`. Écrit ici pour que
 * l'absence de valeur n'écarte pas le devis de la file en silence — un devis
 * sans validité connue est un devis qu'on relance, pas un devis qu'on oublie.
 */
const DEFAULT_VALIDITY_DAYS = 30;

/** Les chantiers sur lesquels du travail est encore attendu. */
const OUVERTS = OPEN_PROJECT_STATUSES;

export function createSupabaseEveningRepository(
  clientFor: DbClientFactory,
  deps: {
    invoices: InvoiceRepository;
    projects: ProjectRepository;
    quotes: QuoteRepository;
    config: TenantConfigRepository;
  },
): EveningRepository {
  return {
    async queue(ctx: ExecutionContext): Promise<EveningItem[]> {
      const [emises, chantiers, devisEnvoyes, configuration] = await Promise.all([
        // Les statuts pour lesquels le client doit encore quelque chose. Le
        // retard se juge ensuite sur `balance`, calculé par le noyau : le
        // refaire ici ferait diverger la file de l'écran des factures.
        deps.invoices.list(ctx, { status: ["sent", "overdue", "partially_paid"] }),
        deps.projects.list(ctx, { status: [...OUVERTS] }),
        deps.quotes.list(ctx, { status: ["sent"] }),
        deps.config.read(ctx),
      ]);

      const impayees = emises.filter((f) => f.balance.daysLate > 0 && f.balance.remaining > 0);
      const ids = impayees.map((f) => f.id);
      const projetIds = chantiers.map((p) => p.id);

      const [
        { data: relances, error: eRelances },
        { data: liens, error: eLiens },
        { data: articles, error: eStock },
      ] = await Promise.all([
        ids.length > 0
          ? clientFor(ctx)
              .from("reminder_actions")
              .select("invoice_id, step_index, status")
              .in("invoice_id", ids)
          : Promise.resolve({ data: [], error: null }),
        projetIds.length > 0
          ? clientFor(ctx)
              .from("projects")
              .select("id, quote_id, quotes!fk_projects_quote(total_ht, total_ttc, quote_number)")
              .in("id", projetIds)
          : Promise.resolve({ data: [], error: null }),
        clientFor(ctx)
          .from("catalog_items")
          .select(
            "id, name, unit, unit_cost, stock_quantity, reorder_threshold, supplier:suppliers(id, name, email)",
          )
          .not("reorder_threshold", "is", null)
          .eq("is_active", true),
      ]);
      if (eRelances) fail(eRelances);
      if (eLiens) fail(eLiens);
      if (eStock) fail(eStock);

      // Le palier le plus élevé réellement **parti**. `skipped` et `failed` ne
      // comptent pas : une relance que Resend a refusée n'a atteint personne,
      // et la retirer de la file laisserait la facture dormir.
      const dernierPalier = new Map<string, number>();
      for (const r of (relances ?? []) as Row[]) {
        if (r.status !== "sent" && r.status !== "delivered") continue;
        const n = num(r.step_index);
        dernierPalier.set(r.invoice_id, Math.max(dernierPalier.get(r.invoice_id) ?? 0, n));
      }

      const contratParProjet = new Map<
        string,
        { quoteId: string; quoteNumber: string; ht: number; ttc: number }
      >();
      for (const l of (liens ?? []) as Row[]) {
        const devis = Array.isArray(l.quotes) ? l.quotes[0] : l.quotes;
        if (devis?.total_ht != null && l.quote_id) {
          contratParProjet.set(l.id as string, {
            quoteId: l.quote_id as string,
            quoteNumber: devis.quote_number ?? "",
            ht: num(devis.total_ht),
            ttc: num(devis.total_ttc),
          });
        }
      }

      const overdueInvoices = impayees.map<OverdueInvoiceSignal>((f) => ({
        invoiceId: f.id,
        invoiceNumber: f.invoiceNumber,
        clientName: f.client ? clientDisplayName(f.client) : "Client inconnu",
        remaining: f.balance.remaining,
        daysLate: f.balance.daysLate,
        lastReminderLevel: dernierPalier.get(f.id) ?? 0,
      }));

      const projects = chantiers.flatMap<ProjectProgressSignal>((p) => {
        const contrat = contratParProjet.get(p.id);
        // Sans devis rattaché, l'avancement n'est chiffrable en euros par
        // aucun bout. Écarté plutôt qu'estimé sur l'enveloppe interne, qui est
        // un objectif de dépense et non un prix de vente.
        if (contrat == null || p.health.progressPercent == null) return [];
        return [
          {
            projectId: p.id,
            projectName: p.name,
            progressPercent: p.health.progressPercent,
            contractHt: contrat.ht,
            contractTtc: contrat.ttc,
            invoicedHt: p.health.invoicedHt ?? 0,
            quoteId: contrat.quoteId,
            quoteNumber: contrat.quoteNumber,
            clientName: p.client ? clientDisplayName(p.client) : "Client inconnu",
            clientEmail: p.client?.email ?? null,
          },
        ];
      });

      // `validity_days` n'est pas dans `QuoteSummary` : il ne sert qu'ici, et
      // l'ajouter au résumé le ferait voyager dans tous les écrans de liste qui
      // n'en ont que faire. Même arbitrage que `quote_id` pour le pilotage.
      const quoteIds = devisEnvoyes.map((q) => q.id);
      let validiteParDevis = new Map<string, number>();
      const derniereRelance = new Map<string, string>();
      if (quoteIds.length > 0) {
        const [{ data: validites, error }, { data: envois, error: eEnvois }] = await Promise.all([
          clientFor(ctx).from("quotes").select("id, validity_days").in("id", quoteIds),
          // Les relances **parties** : un échec Resend n'a relancé personne.
          clientFor(ctx)
            .from("document_sends")
            .select("document_id, created_at")
            .eq("document_type", "quote")
            .eq("kind", "followup")
            .eq("status", "sent")
            .in("document_id", quoteIds),
        ]);
        if (error) fail(error);
        if (eEnvois) fail(eEnvois);
        validiteParDevis = new Map(
          ((validites ?? []) as Row[]).map((q) => [q.id as string, num(q.validity_days)]),
        );
        for (const e of (envois ?? []) as Row[]) {
          const avant = derniereRelance.get(e.document_id);
          if (!avant || e.created_at > avant) derniereRelance.set(e.document_id, e.created_at);
        }
      }

      const quotes = devisEnvoyes.map<PendingQuoteSignal>((q) => {
        // Sans validité connue, on retombe sur celle que la base pose par
        // défaut. Zéro écarterait le devis de la file en silence, ce qui est
        // le contraire du service rendu.
        const validityDays = validiteParDevis.get(q.id) || DEFAULT_VALIDITY_DAYS;
        const relance = derniereRelance.get(q.id) ?? null;
        return {
          quoteId: q.id,
          quoteNumber: q.quoteNumber,
          title: q.title,
          clientName: q.client ? clientDisplayName(q.client) : "Client inconnu",
          totalTtc: q.totals.totalTtc,
          daysSinceSent: daysSince(q.sentAt),
          validityDays,
          validUntil: q.sentAt
            ? new Date(Date.parse(q.sentAt) + validityDays * DAY_MS).toISOString().slice(0, 10)
            : null,
          clientEmail: q.client?.email ?? null,
          daysSinceLastFollowUp: relance ? daysSince(relance) : null,
        };
      });

      const lowStock = ((articles ?? []) as Row[]).map<LowStockSignal>((a) => {
        const f = Array.isArray(a.supplier) ? a.supplier[0] : a.supplier;
        return {
          itemId: a.id,
          name: a.name,
          unit: a.unit ?? "u",
          stockQuantity: num(a.stock_quantity),
          reorderThreshold: num(a.reorder_threshold),
          unitCost: num(a.unit_cost),
          supplierId: f?.id ?? null,
          supplierName: f?.name ?? null,
          supplierEmail: f?.email ?? null,
        };
      });

      return buildEveningQueue({
        overdueInvoices,
        projects,
        quotes,
        lowStock,
        cadence: configuration.reminders,
      });
    },
  };
}
